<script setup lang="ts">
import { getLicenseDetail } from '~/server/detail'
import ArrowRight from '@/assets/icon/arrow-right.svg'

const { t, locale: activeLocale, localePath } = useAppI18n()
const route = useRoute()
const { isRtl } = useRtl()
const { lock: lockPageScroll, unlock: unlockPageScroll } = useStickyDialogScrollLock()
const matomo = useMatomo()
const { CountryCode: countryCode } = useAppStore()

/** 将接口、路由中的未知值转换为已去除首尾空格的字符串。 */
const normalizeTextValue = (value: unknown) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

/** 从可能为数组的路由参数中提取首个有效字符串。 */
const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) return normalizeTextValue(value[0])
  return normalizeTextValue(value)
}
const routeAlias = getFirstRouteValue(route.params.slug)
type RegulationTabKey = 'crypto' | 'other' | 'abnormal'

interface RegulationItem {
  id?: string | number
  title: string
  firstLine?: string
  regulatorShortName?: string
  licenseName?: string
  wikiLicenseName?: string
  subLicenseNumber?: number
  agency?: string
  regulatorFullName?: string
  status?: string
  licenseStatusColor?: string
  regulationId?: string
  licenseNumber?: string
  countryName?: string
  countryFlag?: string
  circleFlag?: string
  licensedEntity?: string
  effectiveTime?: string
  isShare?: string
  badgeImage?: string
  projectCode?: string
}

interface RegulationField {
  key: string
  label: string
  value: string
  flag?: string
  isCountry?: boolean
}

interface AgencyLayoutState {
  clamp: boolean
  fontSize: number
  lineHeight: number
}

interface SingleLineLayoutState {
  fontSize: number
  hideCountry?: boolean
}

interface LicenseTabItem {
  tabName?: string
  tabItems?: Array<{
    countryFlag?: string
    countryName?: string
    regulatorShortName?: string
    regulatorFullName?: string
    licenseName?: string
    simpleName?: string
    firstLine?: string
    licenseStatus?: string
    licenseStatusColor?: string
    regulationId?: string
    regulationNumber?: string
    holdingCompanyName?: string
    effectiveTime?: string
    effectiveDate?: string
    isShare?: string | number | boolean
    licenseType?: string
    subLicenseNumber?: number
    circleFlag?: string
    projectCode?: string
  }>
  tabDesc?: string
  isShowRemain?: boolean
  remainCount?: number
}

interface BusinessInfoTips {
  text?: string
  color?: string
}

interface BusinessInfo {
  showText?: string
  title?: string
  tips?: BusinessInfoTips
  supportDocs?: string[]
  riskWarning?: string
  supportDocCount?: number
}

type RegulationTranslateMap = Partial<
  Record<'RegulationNumber' | 'CountryName' | 'HoldingCompanyName' | 'ShowEffectiveTime' | 'ShareTraderCodes', string>
>

interface ExchangeRegulationCardProps {
  items?: LicenseTabItem[]
  abnormalLicenses?: NonNullable<LicenseTabItem['tabItems']>
  businessInfo?: BusinessInfo | null
  translateMap?: RegulationTranslateMap | null
  projectCode?: string
  emptyDataSourceText?: string
  emptyTitle?: string
  emptySubtitle?: string
  exchangeName?: string
}

const props = withDefaults(defineProps<ExchangeRegulationCardProps>(), {
  items: () => [],
  abnormalLicenses: () => [],
  businessInfo: null,
  translateMap: null,
  exchangeName: '',
})

const emit = defineEmits<{
  open: [item: RegulationItem, items: RegulationItem[], index: number]
}>()

const activeTab = ref<RegulationTabKey>('crypto')
const isNoticeDialogOpen = ref(false)
const tabListRef = ref<HTMLElement | null>(null)
const isTabDragging = ref(false)
const tabButtonMap = new Map<RegulationTabKey, HTMLButtonElement>()
let tabScrollFrame = 0
let tabPointerId: number | null = null
let tabPointerStartX = 0
let tabPointerLastX = 0
let hasTabPointerMoved = false
let tabDragResetTimer = 0
const TAB_DRAG_THRESHOLD = 8
const DEFAULT_LICENSE_STATUS_COLOR = '#1F845A'
const CHINESE_LOCALES = new Set(['zh-cn', 'zh-hk', 'zh-tw', 'zh'])
const COMPACT_LIST_MIN_COUNT = 6

/** 拼接监管简称和牌照名称，缺失字段时保留可用内容作为兜底。 */
const buildRegulationTitle = (item: NonNullable<LicenseTabItem['tabItems']>[number]) => {
  const regulatorShortName = item.regulatorShortName?.trim() || ''
  const wikiLicenseName = item.simpleName?.trim() || item.licenseName?.trim() || ''
  const fallbackTitle = regulatorShortName || wikiLicenseName || '--'

  if (!regulatorShortName || !wikiLicenseName) {
    return fallbackTitle
  }

  return `${regulatorShortName} ${wikiLicenseName}`
}

/** 将接口中的布尔或字符串共享牌照标识转换为当前语言文案。 */
const normalizeIsShareValue = (value: unknown) => {
  if (value === true || normalizeTextValue(value).toLowerCase() === 'true') return t('84190')
  if (value === false || normalizeTextValue(value).toLowerCase() === 'false') return t('84191')
  return normalizeTextValue(value)
}

/** 将接口牌照结构统一映射为卡片内部使用的展示结构。 */
const normalizeApiItem = (item: NonNullable<LicenseTabItem['tabItems']>[number], index: number): RegulationItem => ({
  id: item.regulationId || index,
  title: buildRegulationTitle(item),
  firstLine: item.firstLine || '',
  regulatorShortName: item.regulatorShortName || '',
  licenseName: item.licenseName || '',
  wikiLicenseName: item.simpleName || item.licenseName || '',
  subLicenseNumber: item.subLicenseNumber,
  agency: item.regulatorFullName || item.regulatorShortName || '--',
  regulatorFullName: item.regulatorFullName || '',
  status: item.licenseStatus || '--',
  licenseStatusColor: item.licenseStatusColor || '',
  regulationId: item.regulationId || '',
  licenseNumber: item.regulationNumber || '--',
  countryName: item.countryName || '--',
  countryFlag: item.countryFlag,
  licensedEntity: item.holdingCompanyName || '--',
  effectiveTime: normalizeTextValue(item.effectiveTime || item.effectiveDate) || '--',
  isShare: normalizeIsShareValue(item.isShare ?? item.licenseType) || '--',
  circleFlag: item.circleFlag,
  projectCode: item.projectCode,
})

/** 将异常监管数据映射为与普通监管 tab 一致的展示结构。 */
const abnormalRegulationItems = computed(() =>
  (props.abnormalLicenses || []).map((item, index) => normalizeApiItem(item, index)),
)

const regulationTabs = computed(() => {
  const tabs =
    props.items.length > 0
      ? props.items.map((tab, index) => ({
          key: (index === 0 ? 'crypto' : 'other') as RegulationTabKey,
          label: tab.tabName,
          items: (tab.tabItems || []).map((item, itemIndex) => normalizeApiItem(item, itemIndex)),
          count: tab.tabItems?.length || 0,
          tabDesc: tab.tabDesc || '',
          isShowRemain: Boolean(tab.isShowRemain),
          remainCount: tab.remainCount || 0,
        }))
      : [
          {
            key: 'crypto' as RegulationTabKey,
            label: t('83577'),
            items: [],
            count: 0,
            tabDesc: '',
            isShowRemain: false,
            remainCount: 0,
          },
          {
            key: 'other' as RegulationTabKey,
            label: t('83578'),
            items: [],
            count: 0,
            tabDesc: '',
            isShowRemain: false,
            remainCount: 0,
          },
        ]

  if (!abnormalRegulationItems.value.length) return tabs

  return [
    ...tabs,
    {
      key: 'abnormal' as RegulationTabKey,
      label: t('85765'),
      items: abnormalRegulationItems.value,
      count: abnormalRegulationItems.value.length,
      tabDesc: '',
      isShowRemain: false,
      remainCount: 0,
    },
  ]
})

const activeTabData = computed(
  () => regulationTabs.value.find((tab) => tab.key === activeTab.value) || regulationTabs.value[0],
)
const hasMultipleTabs = computed(() => regulationTabs.value.length > 1)
const activeTabIndex = computed(() => {
  const index = regulationTabs.value.findIndex((tab) => tab.key === activeTab.value)
  return index >= 0 ? index + 1 : 0
})
const activeItems = computed(() => activeTabData.value?.items || [])
const activeItem = computed(() => activeItems.value[0] || null)
const itemCount = computed(() => activeItems.value.length)
const hasActiveRegulation = computed(() => itemCount.value > 0)
const emptyDataSourceText = computed(() => props.emptyDataSourceText || t('83579'))
const emptyTitle = computed(() => props.emptyTitle || t('83581'))
const emptySubtitle = computed(() => props.emptySubtitle || t('83582'))
const hasSingleItem = computed(() => itemCount.value === 1)
const hasTwoItems = computed(() => itemCount.value === 2)
const hasMoreThanFour = computed(() => itemCount.value > 4)
/** useAppI18n 返回普通字符串，直接读取语言代码以正确进入中文布局。 */
const currentLocale = computed(() => normalizeTextValue(activeLocale).toLowerCase())
const isForeignRegulationLayout = computed(() => !CHINESE_LOCALES.has(currentLocale.value))
/** 简繁中文下，单个栏目达到六张牌照时切换为 Figma 单行列表。 */
const usesCompactActiveList = computed(
  () => !isForeignRegulationLayout.value && itemCount.value >= COMPACT_LIST_MIN_COUNT,
)

const expandedSummaryIndex = computed(() => {
  return hasTwoItems.value ? 1 : 0
})

const expandedSummaryItem = computed(() => activeItems.value[expandedSummaryIndex.value] || activeItem.value)

const currentNoticeText = computed(() => props.businessInfo?.showText?.trim() || '')
const showNotice = computed(
  () => activeTabIndex.value === 1 && hasActiveRegulation.value && Boolean(currentNoticeText.value),
)
/** 监管说明保留六条，普通底部提示允许展示七条。 */
const compactListVisibleCount = computed(() => (showNotice.value ? 6 : 7))
const hasExactlySevenFooterItems = computed(
  () => usesCompactActiveList.value && !showNotice.value && itemCount.value === 7,
)
/** 普通布局超过四项、简繁中文单行布局超过当前可见数量时，通过 hover 展开长列表。 */
const hasExpandableLongList = computed(() =>
  usesCompactActiveList.value ? itemCount.value > compactListVisibleCount.value : hasMoreThanFour.value,
)
const noticeDialogData = computed(() => {
  const supportDocs = Array.isArray(props.businessInfo?.supportDocs)
    ? props.businessInfo.supportDocs.filter((item) => typeof item === 'string' && item.trim())
    : []
  const supportDocCount = props.businessInfo?.supportDocCount || supportDocs.length

  return {
    title: props.businessInfo?.title?.trim() || t('83584'),
    headline: props.businessInfo?.tips?.text?.trim() || props.businessInfo?.showText?.trim() || '--',
    headlineColor: props.businessInfo?.tips?.color?.trim() || '#C62828',
    description: props.businessInfo?.riskWarning?.trim() || props.businessInfo?.showText?.trim() || '--',
    proofLabel: t('83583'),
    proofText: t('84541', [supportDocCount]),
    proofIcon: 'https://resources1.interface003.com/web30/2026/06/10/23555258-9754-4b43-a402-3a37ed1d3b06.png',
    buttonText: t('83585'),
    supportDocs,
  }
})
const agencyLayoutMap = ref<Record<string, AgencyLayoutState>>({})
const singleLineLayoutMap = ref<Record<string, SingleLineLayoutState>>({})
const agencyElementMap = new Map<string, HTMLElement>()
const compactRowElementMap = new Map<string, HTMLElement>()
const titleElementMap = new Map<string, HTMLElement>()
let agencyMeasureFrame = 0
let agencyResizeObserver: ResizeObserver | null = null
const canUseDom = typeof window !== 'undefined' && typeof document !== 'undefined'
const agencySizeOptions: AgencyLayoutState[] = [
  { clamp: false, fontSize: 14, lineHeight: 22 },
  { clamp: false, fontSize: 13.5, lineHeight: 21 },
  { clamp: false, fontSize: 13, lineHeight: 20 },
  { clamp: false, fontSize: 12.5, lineHeight: 19 },
  { clamp: false, fontSize: 12, lineHeight: 18 },
  { clamp: false, fontSize: 11.5, lineHeight: 17.5 },
  { clamp: false, fontSize: 11, lineHeight: 17 },
]
const foreignAgencySizeOptions = agencySizeOptions.slice(0, 5)
const DEFAULT_AGENCY_LAYOUT: AgencyLayoutState = { clamp: false, fontSize: 14, lineHeight: 22 }
const MIN_AGENCY_LAYOUT: AgencyLayoutState = { clamp: false, fontSize: 11, lineHeight: 17 }
const MIN_12PX_AGENCY_LAYOUT: AgencyLayoutState = { clamp: false, fontSize: 12, lineHeight: 18 }
const SINGLE_LINE_FONT_SIZES: SingleLineLayoutState['fontSize'][] = [14, 13.5, 13, 12.5, 12, 11.5, 11]
const DEFAULT_SINGLE_LINE_LAYOUT: SingleLineLayoutState = { fontSize: 14 }
const SECOND_LINE_MIN_WIDTH_RATIO = 0.2

/** 按接口字段代码获取已清理空白的多语言标签。 */
const getTranslatedFieldLabel = (code: keyof RegulationTranslateMap) => {
  const label = props.translateMap?.[code]?.trim()
  return label ? `${label}` : ''
}

/** 构建单牌照卡片需要展示的完整字段列表。 */
const buildSingleFields = (item: RegulationItem): RegulationField[] => [
  { key: 'license-number', label: getTranslatedFieldLabel('RegulationNumber'), value: item.licenseNumber || '--' },
  {
    key: 'country',
    label: getTranslatedFieldLabel('CountryName'),
    value: item.countryName || '--',
    flag: item.countryFlag,
    isCountry: true,
  },
  { key: 'licensed-entity', label: getTranslatedFieldLabel('HoldingCompanyName'), value: item.licensedEntity || '--' },
  { key: 'effective-date', label: getTranslatedFieldLabel('ShowEffectiveTime'), value: item.effectiveTime || '--' },
  { key: 'license-type', label: getTranslatedFieldLabel('ShareTraderCodes'), value: item.isShare || '--' },
]

/** 构建多牌照摘要中保留的核心字段列表。 */
const buildMultiFields = (item: RegulationItem): RegulationField[] => [
  { key: 'license-number', label: getTranslatedFieldLabel('RegulationNumber'), value: item.licenseNumber || '--' },
  { key: 'licensed-entity', label: getTranslatedFieldLabel('HoldingCompanyName'), value: item.licensedEntity || '--' },
]

const regulationFields = computed(() => {
  if (!expandedSummaryItem.value) return []
  if (!hasSingleItem.value) return []
  return buildSingleFields(expandedSummaryItem.value)
})
const noticeCertificateTo = computed(() => {
  const exchangeSlug = getFirstRouteValue(route.params.slug)

  if (!exchangeSlug) return ''

  return localePath({
    path: `/exchanges/${exchangeSlug}/licenses`,
    query: {
      from: 'regulation-card',
    },
  })
})

/** 生成列表项稳定 key，并通过前缀隔离不同业务列表。 */
const getItemKey = (item: RegulationItem, index: number, suffix = '') =>
  `${activeTab.value}-${suffix}${String(item.id ?? item.title ?? 'agency')}-${index}`

/** 获取牌照状态色，接口未返回时使用合规绿色兜底。 */
const getLicenseStatusColor = (item: RegulationItem) => item.licenseStatusColor || DEFAULT_LICENSE_STATUS_COLOR

/** 将十六进制状态色转换为带透明度的 rgba 色值。 */
const getStatusAlphaColor = (item: RegulationItem, alpha: number) => {
  const color = getLicenseStatusColor(item).trim()
  const hexMatch = /^#?([0-9a-f]{6})$/i.exec(color)

  if (!hexMatch) return color

  const hex = hexMatch[1]
  if (!hex) return color

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

/** 获取外语布局使用的监管机构简称。 */
const getForeignRegulatorShortName = (item: RegulationItem) => item.regulatorShortName?.trim() || ''
/** 中文列表首行固定展示监管机构英文简称和牌照名称。 */
const getChineseRegulationTitle = (item: RegulationItem) => {
  const regulatorShortName = item.regulatorShortName?.trim() || ''
  const licenseName = item.wikiLicenseName?.trim() || item.licenseName?.trim() || ''
  return [regulatorShortName, licenseName].filter(Boolean).join(' ') || '--'
}
/** 获取外语布局使用的牌照状态文案。 */
const getForeignLicenseStatus = (item: RegulationItem) => item.status || '--'
/** 获取外语布局使用的牌照名称。 */
const getForeignLicenseName = (item: RegulationItem) => item.licenseName?.trim() || ''
/** 拼接外语布局中的国家和牌照名称。 */
const getForeignLicenseLine = (item: RegulationItem) =>
  `${item.countryName?.trim() || ''} ${getForeignLicenseName(item)}`

/** 保存单行牌照文本节点，用于判断国家字段是否需要隐藏。 */
const setCompactRowRef = (key: string) => (element: Element | null) => {
  const previousElement = compactRowElementMap.get(key)
  if (previousElement && previousElement !== element && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }

  if (element instanceof HTMLElement) {
    compactRowElementMap.set(key, element)
    agencyResizeObserver?.observe(element)
    return
  }

  if (previousElement && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }
  compactRowElementMap.delete(key)
  delete singleLineLayoutMap.value[key]
}

/** 保存双行布局中的牌照标题节点，用于按 14px 至 11px 逐级缩放。 */
const setTitleRef = (key: string) => (element: Element | null) => {
  const previousElement = titleElementMap.get(key)
  if (previousElement && previousElement !== element && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }

  if (element instanceof HTMLElement) {
    titleElementMap.set(key, element)
    agencyResizeObserver?.observe(element)
    return
  }

  if (previousElement && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }
  titleElementMap.delete(key)
  delete singleLineLayoutMap.value[key]
}

/** 测量元素在指定字号、单行展示时的实际内容宽度。 */
const measureSingleLineWidth = (element: HTMLElement, fontSize: SingleLineLayoutState['fontSize']) => {
  const clone = element.cloneNode(true) as HTMLElement
  const computedStyle = window.getComputedStyle(element)
  clone.style.position = 'fixed'
  clone.style.left = '-99999px'
  clone.style.top = '0'
  clone.style.width = 'max-content'
  clone.style.maxWidth = 'none'
  clone.style.visibility = 'hidden'
  clone.style.pointerEvents = 'none'
  clone.style.overflow = 'visible'
  clone.style.textOverflow = 'clip'
  clone.style.whiteSpace = 'nowrap'
  clone.style.fontFamily = computedStyle.fontFamily
  clone.style.fontWeight = computedStyle.fontWeight
  clone.style.letterSpacing = computedStyle.letterSpacing
  clone.style.fontSize = `${fontSize}px`
  document.body.appendChild(clone)

  const width = clone.getBoundingClientRect().width
  clone.remove()
  return width
}

/** 单行列表先隐藏国家，再以 0.5px 逐级缩小牌照标题，最小 11px 后省略。 */
const measureCompactRowLayout = (nextMap: Record<string, SingleLineLayoutState>) => {
  compactRowElementMap.forEach((element, key) => {
    const country = element.querySelector<HTMLElement>('[data-compact-country]')
    const title = element.querySelector<HTMLElement>('[data-compact-title]')
    const titleCount = element.querySelector<HTMLElement>('[data-title-count]')

    if (!title || !element.clientWidth) {
      nextMap[key] = DEFAULT_SINGLE_LINE_LAYOUT
      return
    }

    const gap = Number.parseFloat(window.getComputedStyle(element).columnGap) || 0
    const titleWidth = measureSingleLineWidth(title, 14)
    const countryWidth = country ? measureSingleLineWidth(country, 14) : 0
    const titleCountWidth = titleCount ? measureSingleLineWidth(titleCount, 12) : 0
    const visiblePartCount = 1 + Number(Boolean(country)) + Number(Boolean(titleCount))
    const fullContentWidth = countryWidth + titleWidth + titleCountWidth + gap * (visiblePartCount - 1)
    const canShowCountry = !country || fullContentWidth <= element.clientWidth

    if (canShowCountry) {
      nextMap[key] = DEFAULT_SINGLE_LINE_LAYOUT
      return
    }

    const titleAvailableWidth = element.clientWidth - titleCountWidth - (titleCount ? gap : 0)
    const fontSize =
      SINGLE_LINE_FONT_SIZES.find((size) => measureSingleLineWidth(title, size) <= titleAvailableWidth) ?? 11
    nextMap[key] = { fontSize, hideCountry: true }
  })
}

/** 双行布局首行以 0.5px 逐级缩放牌照标题，最小 11px 后保持单行省略。 */
const measureTitleLayout = (nextMap: Record<string, SingleLineLayoutState>) => {
  titleElementMap.forEach((element, key) => {
    const titleElement = element.parentElement
    const titleCount = titleElement?.querySelector<HTMLElement>('[data-title-count]')
    const gap = titleElement ? Number.parseFloat(window.getComputedStyle(titleElement).columnGap) || 0 : 0
    const width =
      (titleElement?.clientWidth || element.clientWidth) - (titleCount?.offsetWidth || 0) - (titleCount ? gap : 0)

    if (!width) {
      nextMap[key] = singleLineLayoutMap.value[key] ?? DEFAULT_SINGLE_LINE_LAYOUT
      return
    }

    const fontSize = SINGLE_LINE_FONT_SIZES.find((size) => measureSingleLineWidth(element, size) <= width) ?? 11
    nextMap[key] = { fontSize }
  })
}

/** 注册监管机构文本节点，供 ResizeObserver 触发自适应测量。 */
const setAgencyRef = (key: string) => (element: Element | null) => {
  const previousElement = agencyElementMap.get(key)
  if (previousElement && previousElement !== element && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }

  if (element instanceof HTMLElement) {
    agencyElementMap.set(key, element)
    agencyResizeObserver?.observe(element)
    return
  }

  if (previousElement && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }
  agencyElementMap.delete(key)
  delete agencyLayoutMap.value[key]
}

/** 测量指定字号下的文本行数和末行宽度占比。 */
const measureAgencyLayout = (element: HTMLElement, layout: AgencyLayoutState) => {
  if (!canUseDom) {
    return {
      lineCount: 1,
      lastLineWidthRatio: 1,
    }
  }

  const width = element.clientWidth
  const text = element.textContent?.trim() || ''

  if (!width || !text) {
    return {
      lineCount: 1,
      lastLineWidthRatio: 1,
    }
  }

  const computedStyle = window.getComputedStyle(element)
  const clone = document.createElement('p')
  clone.textContent = text
  clone.style.position = 'fixed'
  clone.style.left = '-99999px'
  clone.style.top = '0'
  clone.style.visibility = 'hidden'
  clone.style.pointerEvents = 'none'
  clone.style.margin = '0'
  clone.style.padding = '0'
  clone.style.border = '0'
  clone.style.width = `${width}px`
  clone.style.fontFamily = computedStyle.fontFamily
  clone.style.fontWeight = computedStyle.fontWeight
  clone.style.letterSpacing = computedStyle.letterSpacing
  clone.style.fontSize = `${layout.fontSize}px`
  clone.style.lineHeight = `${layout.lineHeight}px`
  clone.style.whiteSpace = 'normal'
  clone.style.wordBreak = 'break-word'
  clone.style.overflowWrap = 'anywhere'

  document.body.appendChild(clone)

  const range = document.createRange()
  range.selectNodeContents(clone)
  const rects = Array.from(range.getClientRects()).filter((rect) => rect.width > 0)

  range.detach?.()
  clone.remove()

  const lineWidths = rects.map((rect) => rect.width)
  const lastLineWidth = lineWidths[lineWidths.length - 1] || width

  return {
    lineCount: Math.max(lineWidths.length, 1),
    lastLineWidthRatio: Math.min(lastLineWidth / width, 1),
  }
}

/** 为当前可见监管机构文本选择最合适的字号与截断策略。 */
const measureAgencyMultiLineMap = () => {
  if (!canUseDom) return

  const nextMap: Record<string, AgencyLayoutState> = {}

  agencyElementMap.forEach((element, key) => {
    const width = element.clientWidth
    if (!width) {
      nextMap[key] = agencyLayoutMap.value[key] ?? DEFAULT_AGENCY_LAYOUT
      return
    }

    const isForeignLicenseLine = key.includes('-license-')
    const sizeOptions = isForeignLicenseLine ? foreignAgencySizeOptions : agencySizeOptions
    const layouts = sizeOptions.map((layout) => ({
      layout,
      measure: measureAgencyLayout(element, layout),
    }))

    if (isForeignLicenseLine) {
      const singleLineLayout = layouts.find(({ measure }) => measure.lineCount <= 1)
      if (singleLineLayout) {
        nextMap[key] = singleLineLayout.layout
        return
      }

      const balancedTwoLineLayout = layouts.find(
        ({ measure }) => measure.lineCount === 2 && measure.lastLineWidthRatio >= SECOND_LINE_MIN_WIDTH_RATIO,
      )
      if (balancedTwoLineLayout) {
        nextMap[key] = balancedTwoLineLayout.layout
        return
      }
    }

    const twoLineLayout = layouts.find(({ measure }) => measure.lineCount <= 2)

    if (twoLineLayout) {
      nextMap[key] = twoLineLayout.layout
      return
    }

    nextMap[key] = {
      ...(isForeignLicenseLine ? MIN_12PX_AGENCY_LAYOUT : MIN_AGENCY_LAYOUT),
      clamp: true,
    }
  })

  agencyLayoutMap.value = nextMap
}

/** 获取监管机构文本当前布局，未完成测量时使用默认字号。 */
const getAgencyLayoutState = (key: string) => agencyLayoutMap.value[key] ?? DEFAULT_AGENCY_LAYOUT

/** 获取单行牌照文本当前布局，未完成测量时使用 14px。 */
const getSingleLineLayoutState = (key: string) => singleLineLayoutMap.value[key] ?? DEFAULT_SINGLE_LINE_LAYOUT

/** 将单行测量结果转换为 Vue 可绑定的字号样式。 */
const getSingleLineScaleStyle = (key: string) => {
  const layout = singleLineLayoutMap.value[key]
  return layout ? { fontSize: `${layout.fontSize}px` } : {}
}

/** 将测量结果转换为 Vue 可绑定的行内样式。 */
const getAgencyScaleStyle = (key: string) => {
  const layout = getAgencyLayoutState(key)
  return {
    display: layout.clamp ? '-webkit-box' : 'block',
    fontSize: `${layout.fontSize}px`,
    lineHeight: `${layout.lineHeight}px`,
    overflow: layout.clamp ? 'hidden' : 'visible',
    textOverflow: layout.clamp ? 'ellipsis' : 'clip',
    WebkitBoxOrient: layout.clamp ? 'vertical' : undefined,
    WebkitLineClamp: layout.clamp ? '2' : undefined,
  }
}

/** 等待 DOM 更新后重新测量监管机构文本布局。 */
const updateAgencyMultiLineMap = async () => {
  if (!canUseDom) return

  await nextTick()

  if (agencyMeasureFrame) {
    cancelAnimationFrame(agencyMeasureFrame)
    agencyMeasureFrame = 0
  }

  measureAgencyMultiLineMap()
  const nextSingleLineMap: Record<string, SingleLineLayoutState> = {}
  measureCompactRowLayout(nextSingleLineMap)
  measureTitleLayout(nextSingleLineMap)
  singleLineLayoutMap.value = nextSingleLineMap
}

/** 保存 tab 按钮节点，用于切换后自动滚动到可视区域。 */
const setTabButtonRef = (key: RegulationTabKey) => (element: Element | null) => {
  if (element instanceof HTMLButtonElement) {
    tabButtonMap.set(key, element)
    return
  }

  tabButtonMap.delete(key)
}

/** 将当前激活 tab 平滑滚动到列表可视区域。 */
const scrollActiveTabIntoView = async (behavior: ScrollBehavior = 'smooth') => {
  if (!canUseDom) return

  await nextTick()

  if (tabScrollFrame) {
    cancelAnimationFrame(tabScrollFrame)
    tabScrollFrame = 0
  }

  tabScrollFrame = requestAnimationFrame(() => {
    const tabList = tabListRef.value
    const activeButton = tabButtonMap.get(activeTab.value)

    if (!tabList || !activeButton) return

    activeButton.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: activeTab.value === 'crypto' ? 'start' : 'center',
    })
  })
}

/** 记录 tab 横向拖拽的起始指针位置。 */
const startTabDrag = (event: PointerEvent) => {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  const tabList = tabListRef.value
  if (!tabList) return

  tabPointerId = event.pointerId
  tabPointerStartX = event.clientX
  tabPointerLastX = event.clientX
  hasTabPointerMoved = false
  if (tabDragResetTimer) {
    window.clearTimeout(tabDragResetTimer)
    tabDragResetTimer = 0
  }
}

/** 根据指针移动距离驱动 tab 列表横向滚动。 */
const dragTabs = (event: PointerEvent) => {
  if (event.pointerId !== tabPointerId) return

  const tabList = tabListRef.value
  if (!tabList) return

  const distance = event.clientX - tabPointerLastX
  if (Math.abs(event.clientX - tabPointerStartX) >= TAB_DRAG_THRESHOLD || hasTabPointerMoved) {
    if (!hasTabPointerMoved) {
      isTabDragging.value = true
      tabList.style.scrollBehavior = 'auto'
      tabList.setPointerCapture(event.pointerId)
    }
    hasTabPointerMoved = true
    tabList.scrollBy({ left: -distance })
  }
  tabPointerLastX = event.clientX
}

/** 结束 tab 拖拽并恢复点击与平滑滚动状态。 */
const endTabDrag = (event: PointerEvent) => {
  if (event.pointerId !== tabPointerId) return

  const tabList = tabListRef.value
  if (tabList?.hasPointerCapture(event.pointerId)) {
    tabList.releasePointerCapture(event.pointerId)
  }
  tabPointerId = null
  isTabDragging.value = false
  tabList?.style.removeProperty('scroll-behavior')
  tabDragResetTimer = window.setTimeout(() => {
    hasTabPointerMoved = false
    tabDragResetTimer = 0
  }, 0)
}

/** 切换监管分类并记录埋点信息。 */
const switchRegulationTab = (key: RegulationTabKey) => {
  if (activeTab.value === key) return
  activeTab.value = key
  matomo.trackEvent('broker_regulator_tab', {
    af_type: activeTabData.value?.label || '',
    af_type1: String(activeTabIndex.value || ''),
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
  })
  scrollActiveTabIntoView()
}

/** 读取未过期的牌照详情本地缓存。 */
const getLicenseData = (regulationId: string) => {
  const key = `${routeAlias}_${currentLocale.value}_${countryCode}_licenseData_${regulationId}`
  const raw = window.localStorage.getItem(key)
  if (!raw) return null
  const { data, expire } = JSON.parse(raw)
  if (Date.now() > expire) {
    window.localStorage.removeItem(key)
    return null
  }
  return data
}

/** 获取牌照详情并写入 24 小时本地缓存。 */
const localStoreLicenseDataFunc = async (regulationId: string) => {
  let licenseDetail = null
  if (getLicenseData(regulationId)) {
    licenseDetail = getLicenseData(regulationId)
  } else {
    licenseDetail = await getLicenseDetail(props.projectCode || '', regulationId, '')
  }
  const LICENSE_CACHE_DURATION = 24 * 60 * 60 * 1000
  window.localStorage.setItem(
    `${routeAlias}_${currentLocale.value}_${countryCode}_licenseData_${regulationId}`,
    JSON.stringify({ data: licenseDetail, expire: Date.now() + LICENSE_CACHE_DURATION }),
  )
}

/** 悬停时预取牌照详情，点击后可直接复用本地缓存。 */
const handleHoverLicense = async (regulationId: string) => {
  await localStoreLicenseDataFunc(regulationId)
}

/** 打开监管说明弹窗。 */
const openNoticeDialog = () => {
  isNoticeDialogOpen.value = true
}

/** 关闭监管说明弹窗。 */
const closeNoticeDialog = () => {
  if (!isNoticeDialogOpen.value) return
  isNoticeDialogOpen.value = false
}

/** 打开牌照详情，并上报当前交易所与牌照信息。 */
const openRegulationItem = (item: RegulationItem, items: RegulationItem[], index: number) => {
  matomo.trackEvent('broker_regulator_click', {
    af_type: activeTabData.value?.label || '',
    af_type1: String(activeTabIndex.value || ''),
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
    af_name1: item.regulatorFullName || '',
    af_code1: item.regulationId || '',
  })
  matomo.trackEvent('licenses_details', {
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
  })
  emit('open', item, items, index)
}

// 切换 tab 或数据源后关闭旧说明弹窗，避免展示与当前分类不一致的内容。
watch(
  () => [activeTab.value, itemCount.value, props.items.length],
  () => {
    isNoticeDialogOpen.value = false
  },
  { immediate: true },
)

// 监管说明弹窗打开时锁定页面滚动，关闭后恢复滚动能力。
watch(isNoticeDialogOpen, (isOpen) => {
  if (isOpen) {
    lockPageScroll()
    return
  }

  unlockPageScroll()
})

// 列表数量变化后重新计算长文本布局。
watch(
  () => [activeTab.value, itemCount.value],
  () => {
    updateAgencyMultiLineMap()
  },
  { immediate: true },
)

// 首次挂载时初始化 tab 位置、文本测量和窗口尺寸监听。
onMounted(() => {
  scrollActiveTabIntoView('auto')

  if (!canUseDom || typeof ResizeObserver === 'undefined') return

  agencyResizeObserver = new ResizeObserver(() => {
    updateAgencyMultiLineMap()
  })
  agencyElementMap.forEach((element) => {
    agencyResizeObserver?.observe(element)
  })
  compactRowElementMap.forEach((element) => {
    agencyResizeObserver?.observe(element)
  })
  titleElementMap.forEach((element) => {
    agencyResizeObserver?.observe(element)
  })
  updateAgencyMultiLineMap()
  // Web Font 就绪后复测，避免回退字体宽度导致国家字段隐藏和字号判断不准确。
  void document.fonts.ready.then(() => updateAgencyMultiLineMap())
  window.addEventListener('resize', updateAgencyMultiLineMap)
})

// 组件卸载时释放动画帧、观察器和全局事件，避免残留副作用。
onBeforeUnmount(() => {
  if (!canUseDom) return

  unlockPageScroll()
  if (tabScrollFrame) {
    cancelAnimationFrame(tabScrollFrame)
  }
  if (tabDragResetTimer) {
    window.clearTimeout(tabDragResetTimer)
  }
  if (agencyMeasureFrame) {
    cancelAnimationFrame(agencyMeasureFrame)
  }
  agencyResizeObserver?.disconnect()
  agencyResizeObserver = null
  window.removeEventListener('resize', updateAgencyMultiLineMap)
})
</script>

<template>
  <section
    class="regulation-card-shell"
    :class="{
      'regulation-card-shell-overflow': hasExpandableLongList,
      'regulation-card-shell-compact': usesCompactActiveList,
      'regulation-card-shell-compact-seven': hasExactlySevenFooterItems,
      'regulation-card-shell-compact-notice': usesCompactActiveList && showNotice,
    }"
  >
    <div class="regulation-card" aria-labelledby="exchange-regulation-title">
      <!-- 监管分类导航：支持横向拖拽，并保持激活项可见。 -->
      <div class="regulation-tabs">
        <div
          ref="tabListRef"
          class="regulation-tab-list"
          :class="{ 'regulation-tab-list-dragging': isTabDragging }"
          role="tablist"
          aria-label="监管类型"
          @pointerdown="startTabDrag"
          @pointermove="dragTabs"
          @pointerup="endTabDrag"
          @pointercancel="endTabDrag"
        >
          <button
            v-for="tab in regulationTabs"
            :id="`exchange-regulation-tab-${tab.key}`"
            :key="tab.key"
            :ref="setTabButtonRef(tab.key)"
            class="regulation-tab"
            :class="{
              'regulation-tab-active': activeTab === tab.key,
              'regulation-tab-active-indicator': activeTab === tab.key && hasMultipleTabs,
              'regulation-tab-single': !hasMultipleTabs,
            }"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.key"
            :aria-controls="`exchange-regulation-panel-${tab.key}`"
            @click="switchRegulationTab(tab.key)"
          >
            <span v-if="tab.key === 'crypto'" id="exchange-regulation-title" class="regulation-tab-label">
              {{ tab.label }}
              <span v-if="tab.count > 0" class="regulation-tab-count">{{ tab.count }}</span>
            </span>
            <span v-else class="regulation-tab-label">
              {{ tab.label }}
              <span v-if="tab.count > 0" class="regulation-tab-count">{{ tab.count }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- 主内容区：按空态、单牌照和多牌照三种数据形态组织展示。 -->
      <div
        :id="`exchange-regulation-panel-${activeTab}`"
        class="regulation-panel"
        role="tabpanel"
        :aria-labelledby="`exchange-regulation-tab-${activeTab}`"
      >
        <div v-if="!activeItem" class="regulation-empty-state">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/07/02/a713a4ab-5436-4099-8dfc-c9a84e2a6a0b.png"
            class="regulation-empty-icon"
            alt=""
            loading="eager"
            object-fit="contain"
          />
          <div class="regulation-empty-copy">
            <p class="regulation-empty-title">{{ emptyTitle }}</p>
            <p class="regulation-empty-subtitle">{{ emptySubtitle }}</p>
          </div>
        </div>

        <div
          v-else
          class="regulation-detail"
          :class="{
            'regulation-detail-scrollable': hasExpandableLongList,
            'regulation-detail-compact': usesCompactActiveList,
          }"
        >
          <div
            class="regulation-summary-list regulation-summary-list-main"
            :class="{
              'regulation-summary-list-main-scroll': hasExpandableLongList,
              'regulation-summary-list-main-exact-four': itemCount === 4,
              'regulation-summary-list-compact': usesCompactActiveList,
            }"
          >
            <template v-for="(item, index) in activeItems" :key="getItemKey(item, index, 'main-')">
              <article
                class="regulation-summary"
                :class="{
                  'regulation-summary-expanded': hasTwoItems && expandedSummaryIndex === index,
                  'regulation-summary-no-divider': itemCount >= 4 && index === activeItems.length - 1,
                }"
                @mouseenter="handleHoverLicense(item.regulationId || '')"
                @click="openRegulationItem(item, activeItems, index)"
              >
                <div class="regulation-summary-content">
                  <template v-if="usesCompactActiveList">
                    <CommonImage
                      :src="item.countryFlag || item.circleFlag"
                      class="compact-regulation-flag"
                      alt=""
                      :loading="index >= compactListVisibleCount ? 'lazy' : 'eager'"
                      object-fit="cover"
                    />
                    <div
                      :ref="setCompactRowRef(getItemKey(item, index, 'main-compact-'))"
                      class="compact-regulation-copy"
                    >
                      <span
                        v-if="item.countryName && item.countryName !== '--'"
                        class="compact-regulation-country"
                        :class="{
                          'compact-regulation-country-hidden': getSingleLineLayoutState(
                            getItemKey(item, index, 'main-compact-'),
                          ).hideCountry,
                        }"
                        data-compact-country
                      >
                        {{ item.countryName }}
                      </span>
                      <span
                        class="compact-regulation-title"
                        data-compact-title
                        :style="getSingleLineScaleStyle(getItemKey(item, index, 'main-compact-'))"
                        >{{ getChineseRegulationTitle(item) }}</span
                      >
                      <span
                        v-if="(item.subLicenseNumber || 0) > 0"
                        class="regulation-summary-title-count"
                        data-title-count
                      >
                        {{ item.subLicenseNumber }}
                      </span>
                    </div>
                    <div class="regulation-summary-side">
                      <span class="regulation-summary-status" :style="{ color: getLicenseStatusColor(item) }">
                        {{ item.status || '--' }}
                      </span>
                      <ArrowRight class="feedback-arrow" :class="{ 'feedback-arrow-rtl': isRtl }" aria-hidden="true" />
                    </div>
                  </template>

                  <template v-else>
                    <CommonImage
                      :src="item.circleFlag"
                      class="regulation-summary-badge"
                      alt=""
                      :loading="index >= 4 ? 'lazy' : 'eager'"
                      object-fit="cover"
                    />

                    <div class="regulation-summary-copy">
                      <template v-if="isForeignRegulationLayout">
                        <h3 class="regulation-summary-title regulation-summary-foreign-title">
                          <span
                            v-if="getForeignRegulatorShortName(item)"
                            class="regulation-summary-short-name"
                            :style="{
                              color: getLicenseStatusColor(item),
                              backgroundColor: getStatusAlphaColor(item, 0.06),
                              borderColor: getStatusAlphaColor(item, 0.2),
                            }"
                          >
                            {{ getForeignRegulatorShortName(item) }}
                          </span>
                          <span
                            class="regulation-summary-license-status"
                            :style="{ color: getLicenseStatusColor(item) }"
                          >
                            {{ getForeignLicenseStatus(item) }}
                          </span>
                          <span v-if="(item.subLicenseNumber || 0) > 0" class="regulation-summary-title-count">
                            {{ item.subLicenseNumber }}
                          </span>
                        </h3>
                        <p
                          :ref="setAgencyRef(getItemKey(item, index, 'main-license-'))"
                          class="regulation-summary-license-line"
                          :style="getAgencyScaleStyle(getItemKey(item, index, 'main-license-'))"
                        >
                          {{ getForeignLicenseLine(item) }}
                        </p>
                      </template>

                      <template v-else>
                        <h3
                          class="regulation-summary-title regulation-summary-chinese-title"
                          :style="{ color: getLicenseStatusColor(item) }"
                        >
                          <span
                            :ref="setTitleRef(getItemKey(item, index, 'main-title-'))"
                            class="regulation-summary-title-text"
                            :style="getSingleLineScaleStyle(getItemKey(item, index, 'main-title-'))"
                          >
                            {{ getChineseRegulationTitle(item) }}
                          </span>
                          <span
                            v-if="(item.subLicenseNumber || 0) > 0"
                            class="regulation-summary-title-count"
                            data-title-count
                          >
                            {{ item.subLicenseNumber }}
                          </span>
                        </h3>
                        <p
                          :ref="setAgencyRef(getItemKey(item, index, 'main-'))"
                          class="regulation-summary-agency"
                          :style="getAgencyScaleStyle(getItemKey(item, index, 'main-'))"
                        >
                          {{ item.agency || '--' }}
                        </p>
                      </template>
                    </div>

                    <div class="regulation-summary-side">
                      <span
                        v-if="!isForeignRegulationLayout"
                        class="regulation-summary-status"
                        :style="{ color: getLicenseStatusColor(item) }"
                        >{{ item.status || '--' }}</span
                      >
                      <ArrowRight class="feedback-arrow" :class="{ 'feedback-arrow-rtl': isRtl }" aria-hidden="true" />
                    </div>
                  </template>
                </div>
              </article>

              <dl
                v-if="hasTwoItems && expandedSummaryIndex === index"
                class="regulation-detail-fields regulation-detail-fields-inline"
              >
                <div
                  v-for="field in buildMultiFields(item)"
                  :key="`${item.id || index}-${field.key}`"
                  class="regulation-field"
                >
                  <dt class="regulation-field-label">{{ field.label }}</dt>
                  <dd class="regulation-field-value">{{ field.value }}</dd>
                </div>
              </dl>
            </template>
          </div>

          <dl v-if="hasSingleItem && regulationFields.length > 0" class="regulation-detail-fields">
            <div v-for="field in regulationFields" :key="field.key" class="regulation-field">
              <dt class="regulation-field-label">{{ field.label }}</dt>
              <dd class="regulation-field-value">
                <span v-if="field.isCountry" class="regulation-field-country">
                  <CommonImage
                    v-if="field.flag"
                    :src="field.flag"
                    class="regulation-country-flag"
                    alt=""
                    loading="eager"
                    object-fit="cover"
                  />
                  <span>{{ field.value }}</span>
                </span>
                <span v-else>{{ field.value }}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- 底部根据接口说明数据切换普通提示或可点击的监管说明入口。 -->
      <div v-if="!showNotice" class="regulation-footer" :aria-label="emptyDataSourceText">
        <div class="box">
          <span class="regulation-footer-line" aria-hidden="true"></span>
          <span class="regulation-footer-text">{{ emptyDataSourceText }}</span>
          <span class="regulation-footer-line" aria-hidden="true"></span>
        </div>
      </div>

      <button v-if="showNotice" class="regulation-notice" type="button" @click="openNoticeDialog">
        <span class="regulation-notice-copy">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/07/14/1a26d1db-7054-468d-92cd-c2d4ef56a078.png"
            class="regulation-notice-icon"
            alt=""
            loading="eager"
            object-fit="contain"
          />
          <span class="regulation-notice-text">{{ currentNoticeText }}</span>
        </span>
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/07/14/80bda966-72b5-4b43-ae19-160f62c7333e.png"
          class="regulation-notice-arrow"
          alt=""
          loading="eager"
          object-fit="contain"
        />
      </button>

      <!-- 监管说明弹窗展示当前 tab 对应的风险提示内容。 -->
      <Teleport to="body">
        <div v-if="isNoticeDialogOpen" class="notice-dialog-mask">
          <section class="notice-dialog" role="dialog" aria-modal="true" aria-labelledby="notice-dialog-title">
            <header class="notice-dialog-header">
              <h2 id="notice-dialog-title" class="notice-dialog-title">{{ noticeDialogData.title }}</h2>
              <button type="button" class="notice-dialog-close" aria-label="关闭交易所弹窗" @click="closeNoticeDialog">
                <CommonImage
                  class="size-[32px]"
                  src="https://resources1.interface003.com/web30/2026/06/08/2af6f5ee-7ed0-4d4b-8dff-dcf5540ccb14.png"
                  alt=""
                  object-fit="contain"
                />
              </button>
            </header>

            <div class="notice-dialog-body">
              <div class="notice-dialog-content">
                <div class="notice-dialog-copy">
                  <p class="notice-dialog-headline" :style="{ color: noticeDialogData.headlineColor }">
                    {{ noticeDialogData.headline }}
                  </p>
                  <div class="notice-dialog-description-wrap">
                    <p class="notice-dialog-description">{{ noticeDialogData.description }}</p>
                  </div>
                  <!-- <div class="notice-dialog-proof">
                    <span class="notice-dialog-proof-label">{{ noticeDialogData.proofLabel }}</span>
                    <NuxtLink
                      v-if="noticeCertificateTo"
                      class="notice-dialog-proof-link"
                      :to="noticeCertificateTo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CommonImage
                        :src="noticeDialogData.proofIcon"
                        class="notice-dialog-proof-icon"
                        alt=""
                        loading="eager"
                        object-fit="contain"
                      />
                      <span>{{ noticeDialogData.proofText }}</span>
                    </NuxtLink>
                  </div> -->
                </div>

                <button type="button" class="notice-dialog-confirm" @click="closeNoticeDialog">
                  {{ noticeDialogData.buttonText }}
                </button>
              </div>
            </div>
          </section>
        </div>
      </Teleport>
    </div>
  </section>
</template>

<style scoped lang="less">
// 卡片外壳负责角标定位，并在长列表场景为纵向滚动预留空间。
.regulation-card-shell {
  position: relative;
  width: 100%;
  min-height: 418px;
  overflow: visible;

  &.regulation-card-shell-overflow {
    .regulation-card {
      transition: box-shadow 0.28s cubic-bezier(0.22, 1, 0.36, 1);
      padding-bottom: 0;
    }

    .regulation-summary-list-main:not(.regulation-summary-list-compact) > .regulation-summary:nth-of-type(n + 5),
    .regulation-summary-list-compact > .regulation-summary:nth-of-type(n + 8) {
      display: none;
    }

    .regulation-footer {
      display: none;
    }

    .regulation-summary-list-main:not(.regulation-summary-list-compact)
      > .regulation-summary:nth-of-type(4)
      .regulation-summary-content,
    .regulation-summary-list-compact > .regulation-summary:nth-of-type(7) .regulation-summary-content {
      box-shadow: none;
    }

    &.regulation-card-shell-compact-notice {
      .regulation-summary-list-compact > .regulation-summary:nth-of-type(n + 7) {
        display: none;
      }

      .regulation-summary-list-compact > .regulation-summary:nth-of-type(6) .regulation-summary-content {
        box-shadow: none;
      }
    }

    &:has(.regulation-detail:hover) {
      z-index: 99;

      .regulation-card {
        position: absolute;
        top: 0;
        inset-inline: 0;
        height: auto;
        overflow: hidden;
        box-shadow:
          inset 0 0 0 0.5px rgba(11, 18, 40, 0.14),
          0 0 0 8px #ebf2f3;
        padding-bottom: 0px;
      }

      .regulation-panel {
        min-height: 0;
        flex: 1 1 auto;
      }

      .regulation-detail {
        min-height: 0;
        flex: 1 1 auto;
        overflow-y: auto;
      }

      .regulation-summary-list-main {
        min-height: auto;
        flex: 0 0 auto;
      }

      .regulation-summary-list-main > .regulation-summary {
        display: flex;
        min-height: auto;
        max-height: none;
        flex: 0 0 auto;
        padding: 20px 0;
      }

      .regulation-summary-list-main-scroll > .regulation-summary {
        padding: 0 8px;
      }

      .regulation-summary-list-main:not(.regulation-summary-list-compact)
        > .regulation-summary:nth-of-type(4)
        .regulation-summary-content,
      .regulation-summary-list-compact > .regulation-summary:nth-of-type(7) .regulation-summary-content {
        box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
      }

      .regulation-footer {
        display: flex;
        margin-top: 10px;
      }

      .regulation-notice {
        margin-top: 10px;
      }
    }

    &.regulation-card-shell-compact-notice:has(.regulation-detail:hover)
      .regulation-summary-list-compact
      > .regulation-summary:nth-of-type(6)
      .regulation-summary-content {
      box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
    }
  }

  &.regulation-card-shell-compact-seven .regulation-footer {
    display: none;
  }

  .regulation-summary-list-main:not(.regulation-summary-list-compact) .regulation-summary-content {
    box-sizing: content-box;
    height: 48px;
  }

  &:has(.regulation-notice) .regulation-summary-list-main:not(.regulation-summary-list-compact) {
    > .regulation-summary {
      height: 75px;
      flex: 0 0 75px;
    }

    .regulation-summary-content {
      padding-block: 13.5px;
    }
  }

  &:has(.regulation-footer) .regulation-summary-list-main:not(.regulation-summary-list-compact) {
    > .regulation-summary {
      height: 89px;
      flex: 0 0 89px;
    }

    .regulation-summary-content {
      padding-block: 20px;
    }

    &.regulation-summary-list-main-exact-four {
      > .regulation-summary {
        height: 79px;
        flex: 0 0 79px;
      }

      .regulation-summary-content {
        padding-block: 15.5px;
      }
    }
  }
}

.regulation-card {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 418px;
  height: auto;
  max-height: 700px;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 0 0 0.5px rgba(11, 18, 40, 0.14);
  padding: 4px 12px 0 12px;
}

.regulation-tabs {
  display: flex;
  width: 100%;
  padding: 0 6px;
  align-items: center;
  justify-content: space-between;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
}

.regulation-tab-list {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  gap: 20px;
  padding-inline: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-padding-inline: 2px;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.regulation-tab-list::-webkit-scrollbar {
  display: none;
}

.regulation-tab-list-dragging {
  cursor: grabbing;
  user-select: none;
}

.regulation-tab {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  min-height: 58px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #7d818a;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  padding: 16px 0;
  white-space: nowrap;
  scroll-snap-align: start;
  cursor: pointer;
  &:hover {
    color: var(--color-text, #292a2e);
  }
}

.regulation-tab-single {
  cursor: default;
}

.regulation-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.regulation-tab-count {
  color: #7d818a;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 0 4px;
  height: 20px;
  border-radius: 1000px;
  background: #f8f8f8;
  min-width: 20px;
}

.regulation-tab-active {
  color: #292a2e;
  font-weight: 600;
}

.regulation-tab-active-indicator::after {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 28px;
  height: 4px;
  border-radius: 999px;
  background: #292a2e;
  content: '';
  transform: translateX(-50%);
}

.regulation-panel {
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.regulation-empty-state {
  display: flex;
  width: 100%;
  min-height: 276px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border: 1px dashed rgba(5, 21, 36, 0.06);
  border-radius: 8px;
  background: #f8f8f8;
  margin-top: 20px;
  padding: 16px;
}

.regulation-empty-icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.regulation-empty-copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.regulation-empty-title,
.regulation-empty-subtitle {
  margin: 0;
}

.regulation-empty-title {
  color: #292a2e;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}

.regulation-empty-subtitle {
  color: #7d818a;
  font-size: 16px;
  line-height: 24px;
}

.regulation-detail {
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.regulation-detail-scrollable {
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.regulation-detail-scrollable::-webkit-scrollbar {
  display: none;
}

.regulation-detail-compact {
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.regulation-detail-compact::-webkit-scrollbar {
  display: none;
}

.regulation-summary-list {
  display: flex;
  width: 100%;
  flex-direction: column;
}

.regulation-summary-list-main {
  min-height: 0;
  flex: 0 0 auto;
}

.regulation-summary-list-main-scroll {
  flex: 1 1 auto;
}

.regulation-summary-list-main > .regulation-summary {
  height: 89px;
}

.regulation-summary-list-main-exact-four > .regulation-summary {
  height: 79px;
}

.regulation-summary-list-main-exact-four .regulation-summary-content {
  padding-top: 15px;
  padding-bottom: 15px;
}

.regulation-summary-list-main-scroll > .regulation-summary {
  flex: 1 1 0;
  border-radius: 4px;
}

.regulation-summary-list-compact > .regulation-summary {
  height: 50px;
  flex: 0 0 50px;
}

.regulation-summary-list-compact .regulation-summary-content {
  gap: 8px;
  padding: 14px 0;
}

.regulation-summary {
  display: flex;
  width: 100%;
  border: 0;
  background: transparent;
  font: inherit;
  padding: 0 8px;
  text-align: left;
  cursor: pointer;
}

.regulation-summary:hover {
  background: #f8f8f8;
}

.regulation-summary-content {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  align-items: center;
  gap: 12px;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
  border-radius: 4px;
  padding: 20px 0;
  overflow: hidden;
}

.regulation-summary-no-divider .regulation-summary-content {
  box-shadow: none;
}

.regulation-summary-badge {
  display: block;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  object-fit: cover;
  border-radius: 40px;
  overflow: hidden;
}

.regulation-summary-copy {
  min-width: 0;
  flex: 1 0 0;
}

.regulation-summary-title {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.regulation-summary-title {
  color: #1f845a;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.regulation-summary-title-count {
  display: inline-flex;
  flex: 0 0 auto;
  height: 14px;
  align-items: center;
  justify-content: center;
  line-height: 14px;
  border-radius: 2px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  padding: 0 3px;
  color: #7d818a;
  font-size: 12px;
}

.regulation-summary-chinese-title {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
  overflow: visible;
  text-overflow: clip;
}

.regulation-summary-title-text {
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.regulation-summary-foreign-title {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
}

.regulation-summary-short-name {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
  height: 20px;
  align-items: center;
  justify-content: center;
  border: 0.5px solid;
  border-radius: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1f845a;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 0 4px;
}

.regulation-summary-license-status {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
}

.regulation-summary-license-line {
  display: block;
  width: 100%;
  margin: 4px 0 0;
  overflow: visible;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
}

.regulation-summary-agency {
  margin: 0;
  margin-top: 4px;
  color: #292a2e;
  font-size: 14px;
  line-height: 22px;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.regulation-summary-side {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
}

.compact-regulation-flag {
  display: block;
  width: 24px;
  height: 16px;
  flex: 0 0 24px;
  border-radius: 2px;
  object-fit: cover;
  overflow: hidden;
}

.compact-regulation-copy {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  gap: 4px;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
}

.compact-regulation-country {
  flex: 0 0 auto;
}

.compact-regulation-country-hidden {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
}

.compact-regulation-title {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.regulation-summary-status {
  display: inline-block;
  color: #1f845a;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.regulation-notice-arrow {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}

.feedback-arrow {
  width: 20px;
  height: 20px;
  color: #7d818a;
  &:hover {
    color: #292a2e;
  }
}

.feedback-arrow-rtl {
  transform: rotate(180deg);
}

.feedback-arrow :deep(path) {
  fill: currentColor;
}

.regulation-detail-fields {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
  margin: 0;
  padding: 20px 8px;
}

.regulation-field {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.regulation-field-label {
  flex-shrink: 0;
  color: #7d818a;
}

.regulation-field-label,
.regulation-field-value {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

.regulation-field-value {
  min-width: 0;
  overflow: hidden;
  color: #292a2e;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}

.regulation-field-country {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.regulation-country-flag {
  display: block;
  width: 22px;
  height: 14px;
  flex: 0 0 22px;
  object-fit: cover;
}

.regulation-notice {
  display: flex;
  width: calc(100% - 16px);
  align-items: center;
  justify-content: space-between;
  border: 0;
  border-radius: 4px;
  background: rgba(201, 55, 44, 0.06);
  margin-inline: 8px;
  padding: 8px 12px;
  cursor: pointer;
  margin-bottom: 20px;
}

.regulation-notice-copy {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
}

.regulation-notice-icon {
  display: block;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  object-fit: contain;
}

.regulation-notice-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #292a2e;
  font-size: 12px;
  line-height: 18px;
}

.regulation-footer {
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  .box {
    width: 262px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.regulation-footer-line {
  height: 0;
  flex: 1 0 0;
  border-top: 0.5px solid rgba(5, 21, 36, 0.06);
}

.regulation-footer-text {
  color: #b7b9be;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}

.feedback-arrow {
  width: 20px;
  height: 20px;
  color: #7d818a;
  &:hover {
    color: #292a2e;
  }
}

.feedback-arrow-rtl {
  transform: rotate(180deg);
}

.feedback-arrow :deep(path) {
  fill: currentColor;
}

.notice-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(11, 18, 40, 0.36);
  padding: 24px;
}

.notice-dialog {
  width: 540px;
  max-width: calc(100vw - 48px);
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

.notice-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 16px;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
}

.notice-dialog-title {
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.notice-dialog-close {
  position: relative;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  padding: 0;
}

.notice-dialog-close-mark {
  position: relative;
  display: block;
  width: 12px;
  height: 12px;
}

.notice-dialog-close-mark::before,
.notice-dialog-close-mark::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 1.5px;
  border-radius: 999px;
  background: #7d818a;
  content: '';
  transform-origin: center;
}

.notice-dialog-close-mark::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.notice-dialog-close-mark::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.notice-dialog-body {
  padding: 20px 32px 32px;
}

.notice-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

.notice-dialog-copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
}

.notice-dialog-headline {
  margin: 0;
  color: #e2483d;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}

.notice-dialog-description-wrap {
  border-radius: 4px;
  background: #f8f8f8;
  padding: 16px;
}

.notice-dialog-description {
  margin: 0;
  color: #292a2e;
  font-size: 14px;
  line-height: 22px;
}

.notice-dialog-proof {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
}

.notice-dialog-proof-label {
  color: #7d818a;
  font-size: 14px;
  line-height: 22px;
}

.notice-dialog-proof-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #0d3f63;
  font-size: 14px;
  line-height: 22px;
  text-decoration: none;
  cursor: pointer;
}

.notice-dialog-proof-link-disabled {
  cursor: default;
  opacity: 0.6;
}

.notice-dialog-proof-icon {
  display: block;
  width: 16px;
  height: 16px;
}

.notice-dialog-confirm {
  min-width: 184px;
  height: 46px;
  border: 0;
  border-radius: 999px;
  background: #112d73;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  padding: 0 20px;
}
html[dir='rtl'] {
  .regulation-card-shell {
    .regulation-summary-license-line {
      text-align: right;
    }
  }
  .regulation-notice-arrow {
    transform: rotate(180deg);
  }
}
</style>
